import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, RefreshCcw, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import Logo from "../components/Logo";
import StatsGrid from "../components/admin/StatsGrid";
import ApplicationsTable from "../components/admin/ApplicationsTable";
import ApplicationDetailDialog from "../components/admin/ApplicationDetailDialog";
import { APPLICATION_STATUSES } from "../lib/applicationStatus";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

function filterApplications(apps, query, statusFilter) {
  const s = query.trim().toLowerCase();
  return apps.filter((a) => {
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    if (!s) return true;
    const hay = `${a.first_name} ${a.last_name} ${a.reason_for_loan} ${a.a_name || ""} ${a.b_name} ${a.r_name} ${a.identifier} ${a.identification} ${a.id}`.toLowerCase();
    return hay.includes(s);
  });
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/applications"),
        api.get("/admin/stats"),
      ]);
      setApps(a.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ready && user && user.role === "admin") load();
  }, [ready, user, load]);

  const filtered = useMemo(
    () => filterApplications(apps, q, statusFilter),
    [apps, q, statusFilter],
  );

  const updateStatus = useCallback(
    async (id, next) => {
      setUpdating(true);
      try {
        const { data } = await api.patch(`/admin/applications/${id}/status`, { status: next });
        setApps((list) => list.map((a) => (a.id === id ? data : a)));
        setSelected((curr) => (curr && curr.id === id ? data : curr));
        const s = await api.get("/admin/stats");
        setStats(s.data);
        toast.success(`Status updated to "${next}".`);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setUpdating(false);
      }
    },
    [],
  );

  const doLogout = useCallback(async () => {
    await logout();
    navigate("/admin/login");
  }, [logout, navigate]);

  const confirmDelete = useCallback(async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/applications/${toDelete.id}`);
      setApps((list) => list.filter((a) => a.id !== toDelete.id));
      setSelected((curr) => (curr && curr.id === toDelete.id ? null : curr));
      const s = await api.get("/admin/stats");
      setStats(s.data);
      toast.success("Application deleted.");
      setToDelete(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  }, [toDelete]);

  if (ready && (!user || user.role !== "admin")) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#050A1F] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#050A1F]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] sm:block">
              Admin · Console
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden text-right sm:block">
              <div className="text-xs text-white/50">Signed in as</div>
              <div className="text-sm text-white" data-testid="admin-user-email">
                {user?.email}
              </div>
            </div>
            <button
              onClick={doLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[10px] uppercase tracking-[0.28em] text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Loan Applications</div>
            <h1 className="mt-2 font-serif text-4xl font-light sm:text-5xl">Dashboard</h1>
            <p className="mt-2 text-sm text-white/60">Review, triage and progress client applications.</p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[10px] uppercase tracking-[0.28em] text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        <StatsGrid stats={stats} />

        <div className="mt-10 flex flex-col gap-4 border border-white/10 bg-white/[0.02] p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, id, reason, code…"
              data-testid="admin-search-input"
              className="w-full border border-white/15 bg-[#050A1F] pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            />
          </div>
          <div className="w-full sm:w-64">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                data-testid="admin-status-filter"
                className="w-full rounded-none border-white/15 bg-[#050A1F] text-sm text-white"
              >
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="border-white/15 bg-[#0A1128] text-white">
                <SelectItem value="all">All statuses</SelectItem>
                {APPLICATION_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <ApplicationsTable apps={filtered} loading={loading} onView={setSelected} onDelete={setToDelete} />

        <div className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/40">
          <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span>All records encrypted at rest · Audit-logged</span>
        </div>
      </main>

      <ApplicationDetailDialog
        app={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onUpdateStatus={updateStatus}
        updating={updating}
        onDelete={setToDelete}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && !deleting && setToDelete(null)}>
        <AlertDialogContent
          className="rounded-none border-white/15 bg-[#0A1128] text-white"
          data-testid="delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-light">
              Delete this application?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {toDelete && (
                <>
                  You are about to permanently remove{" "}
                  <span className="text-[#D4AF37]">
                    {toDelete.first_name} {toDelete.last_name}
                  </span>
                  's application (<span className="font-mono">{toDelete.id.slice(0, 8)}…</span>).
                  This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel-btn"
              disabled={deleting}
              className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              data-testid="delete-confirm-btn"
              disabled={deleting}
              className="rounded-none border border-red-400 bg-red-500/20 text-red-100 hover:bg-red-500/30"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
